/**
 * Dashboard (super-admin only) — a top-level section pulled out of Admin. Tabs:
 *   • Observability — the live desktop activity / render jobs / exports / per-job
 *     traces board (the existing dashboard/Dashboard component).
 *   • Health        — is everything up? Cloud API + DB, AI providers, hosting/CDN,
 *     per-device desktop health, and live job throughput.
 *   • Error log     — failed / interrupted jobs (drill into the trace).
 *   • Activity log  — every job (drill into the full flow + per-item load/response).
 * Server gates the underlying telemetry reads; this only renders for a super-admin.
 */
import { useEffect, useState, type ReactNode } from "react";
import { EmptyState } from "../ui/EmptyState.js";
import { Tabs } from "../ui/Tabs.js";
import { useServices } from "../app/context.js";
import { Drawer } from "../ui/Drawer.js";
import { CopyButton } from "../ui/CopyButton.js";
import { Dashboard } from "../dashboard/Dashboard.js";
import { clearAllCache } from "../stores/cache.js";
import { clearModelCache } from "../stores/modelStore.js";
import { useCachedResource } from "../stores/useCachedResource.js";
import type { AiCallLogEntry, AiUsage, ClientLogRow, GenerationJobView, HeartbeatPayload, JobRecord, LogEntry, TraceEvent } from "@genz/platform";
// Cloud timestamps: reads a timezone-less value (an older cloud) as UTC rather than local.
import { isoToMs } from "@genz/platform";
import "./observability.css";
import { RowAction } from "../ui/RowAction.js";

function fmtCost(usd: number | null | undefined): string {
  if (usd == null) return "—";
  return `$${usd.toFixed(usd < 0.01 ? 4 : 2)}`;
}

/** One label|value row inside a `.fact-rows` box (omit the row entirely when empty). */
function Fact({ label, value, testid }: { label: string; value: ReactNode; testid?: string }) {
  return (
    <div className="fact-row" data-testid={testid}>
      <span className="fact-row__label">{label}</span>
      <span className="fact-row__value">{value}</span>
    </div>
  );
}

type Tab = "observability" | "health" | "errors" | "activity" | "aicalls";

function fmtTime(ms: number | undefined): string {
  if (!ms) return "—";
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}
function fmtDur(ms: number | undefined): string {
  if (ms == null) return "—";
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}
function statusClass(s: string): string {
  return s === "completed" ? "badge--success"
    : s === "failed" || s === "interrupted" ? "badge--danger"
    : s === "running" ? "badge--info"
    : "badge--neutral";
}

const TABS: { key: Tab; label: string; hint: string }[] = [
  { key: "observability", label: "Observability", hint: "Activity & traces" },
  { key: "health", label: "Health", hint: "Connections & devices" },
  { key: "errors", label: "Error log", hint: "Failures only" },
  { key: "activity", label: "Activity log", hint: "All jobs" },
  { key: "aicalls", label: "AI calls", hint: "Prompts & responses" },
];

export function DashboardSection() {
  const [tab, setTab] = useState<Tab>("observability");
  return (
    <section className="dash-section" data-testid="dashboard-section">
      <div className="panel-head">
        <div>
          <h1 className="panel-title">Dashboard</h1>
          <p className="panel-sub">Live observability, the health of every connection, and the job logs.</p>
        </div>
      </div>

      <Tabs
        tabs={TABS}
        active={tab}
        onChange={(k) => setTab(k as Tab)}
        label="Dashboard"
        idPrefix="dashboard"
      >
        {tab === "observability" && <Dashboard />}
        {tab === "health" && <HealthPanel />}
        {tab === "errors" && <LogPanel mode="errors" />}
        {tab === "activity" && <LogPanel mode="activity" />}
        {tab === "aicalls" && <AiCallsPanel />}
      </Tabs>
    </section>
  );
}


/** AI calls log — the exact prompt SENT and metadata RECEIVED for each brokered call. */
function AiCallsPanel() {
  const { client } = useServices();
  const [open, setOpen] = useState<AiCallLogEntry | null>(null);
  // Cache-first: show the last-known calls instantly; revalidate quietly every 60s.
  const { data, loading, error, revalidate } = useCachedResource<AiCallLogEntry[]>(
    "dash.aicalls", () => client.aiCalls(100),
  );
  const rows = data ?? [];
  const refresh = revalidate;

  return (
    <div data-testid="obs-aicalls">
      <div className="obs-stats">
        <button type="button" className="btn btn--sm" onClick={() => void refresh()} disabled={loading} data-testid="aicalls-refresh">
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>
      {error && <p role="alert">{error}</p>}
      {rows.length === 0 ? (
        <EmptyState icon="list" title={loading ? "Loading…" : "No AI calls yet"} hint="Every brokered generation will be logged here with its prompt + response." />
      ) : (
        <>
          <div className="table-scroll">
          <table className="table obs-table--click" data-testid="aicalls-table">
            <thead><tr><th>Status</th><th>Provider / model</th><th className="obs-num">Images</th><th className="obs-num">Tokens</th><th className="obs-num">Latency</th><th>When</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid="aicall-row">
                  <td><span className={`badge badge--plain ${r.status === "ok" ? "badge--success" : "badge--danger"}`}>{r.status}</span></td>
                  <td>
                    <button type="button" className="link-btn" onClick={() => setOpen(r)} data-testid="aicall-open">
                      {r.provider ?? "—"} · <code>{r.model ?? "—"}</code>
                    </button>
                  </td>
                  <td className="obs-num">{r.reference_count}r / {r.product_count}p</td>
                  <td className="obs-num">{(r.input_tokens ?? 0) + (r.output_tokens ?? 0) || "—"}</td>
                  <td className="obs-num">{r.latency_ms != null ? `${r.latency_ms}ms` : "—"}</td>
                  <td className="faint">{fmtTime(isoToMs(r.created_at) || undefined)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <p className="obs-note">
            Image bytes are relayed to the client and never stored (pixel guard). Detection runs
            on-device — only the optional <b>generate</b> step is brokered here.
          </p>
        </>
      )}

      {/* WS-Z4 / R1: the SHARED drawer. This was a hand-rolled `.lightbox` with
          `role="dialog" aria-modal` — the second bespoke drawer in this file, and the twin of
          the one in `dashboard/Dashboard.tsx`. `.lightbox` is image zoom; a detail view is a
          `Drawer`, which is also where Escape-to-close and focus-on-open come from. The
          hand-rolled "Close" is gone and the drawer's own close control is its survivor; every
          fact, prompt and response field below is unchanged. */}
      {open && (
        <Drawer
          title={<>AI call — {open.provider} · <code>{open.model}</code></>}
          subtitle={`${open.size ?? "default size"} · ${open.quality ?? "default quality"} · ${open.reference_count} ref / ${open.product_count} product · ${open.latency_ms != null ? `${open.latency_ms}ms` : "—"} · ${fmtTime(isoToMs(open.created_at) || undefined)}`}
          badge={<span className={`badge ${open.status === "ok" ? "badge--success" : "badge--danger"}`}>{open.status}</span>}
          onClose={() => setOpen(null)}
          testid="aicall-drawer"
        >
          <div className="fact-rows" data-testid="aicall-facts">
            <Fact label="Call id" value={<><code>{open.id}</code> <CopyButton value={open.id} label="Copy call id" /></>} testid="aicall-id" />
            {open.owner_email && <Fact label="Owner" value={open.owner_email} testid="aicall-owner" />}
            <Fact label="Tokens (in)" value={open.input_tokens != null ? open.input_tokens.toLocaleString() : "—"} testid="aicall-tokens-in" />
            <Fact label="Tokens (out)" value={open.output_tokens != null ? open.output_tokens.toLocaleString() : "—"} testid="aicall-tokens-out" />
            {open.cost_usd != null && <Fact label="Cost" value={fmtCost(open.cost_usd)} testid="aicall-cost" />}
          </div>
          {open.error && <><h4>Error</h4><pre className="obs-trace__items obs-trace__items--error">{open.error}</pre></>}
          <h4>Prompt — system</h4>
          <pre className="obs-trace__items">{open.prompt_system || "—"}</pre>
          <h4>Prompt — user (sent)</h4>
          <pre className="obs-trace__items">{open.prompt_user || "—"}</pre>
          <h4>Response (received)</h4>
          <pre className="obs-trace__items">{JSON.stringify({
            media_type: open.media_type, output_bytes: open.output_bytes,
            input_tokens: open.input_tokens, output_tokens: open.output_tokens,
          }, null, 2)}</pre>
          <p className="faint">Image bytes are relayed to the client and never stored (pixel guard).</p>
        </Drawer>
      )}
    </div>
  );
}

/** Human label for the row source (which authoritative feed it came from). */
const KIND_LABEL: Record<LogEntry["kind"], string> = {
  generation: "generation",
  ai_call: "ai_call",
  render: "mock-up",
  export: "export",
};

/**
 * Activity / Error log — upgraded explorer (Task 12). Combines:
 *  • The unified, server-authoritative feed (dashboardLog) — generation jobs,
 *    AI calls, render jobs, exports.
 *  • The new client-log feed (dashboardClientLogs) — structured client events
 *    captured by ClientLogger (errors, network requests, breadcrumbs, etc.).
 * Filters: customer, device, level, category, time range; full-text search (q).
 * Row click → detail drawer for the appropriate feed.
 */
function LogPanel({ mode }: { mode: "activity" | "errors" }) {
  const { client, logger } = useServices();

  // Server-derived rows (existing feed)
  const [openRow, setOpenRow] = useState<LogEntry | null>(null);
  const { data, loading, error, revalidate } = useCachedResource<LogEntry[]>(
    `dash.log.${mode}`, () => client.dashboardLog({ mode }),
  );
  const serverRows = data ?? [];

  // Task 8: while any job is queued or running, refresh the unified feed every 10s
  // so the panel reflects live progress (Task 1 made these statuses real). Layered
  // ON TOP of useCachedResource — when only terminal rows remain, the interval is
  // torn down and the existing lazy 60s cadence takes over.
  const hasActiveJobs = serverRows.some((r) => r.status === "queued" || r.status === "running");
  useEffect(() => {
    if (!hasActiveJobs) return;
    const id = setInterval(() => { void revalidate(); }, 10_000);
    return () => clearInterval(id);
  }, [hasActiveJobs, revalidate]);

  // Client-log filter state
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterDevice, setFilterDevice] = useState("");
  const [filterLevel, setFilterLevel] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterSince, setFilterSince] = useState("");
  const [filterUntil, setFilterUntil] = useState("");
  const [searchQ, setSearchQ] = useState("");

  // Client-log rows fetched from dashboardClientLogs
  const [clientRows, setClientRows] = useState<ClientLogRow[]>([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientError, setClientError] = useState<string | null>(null);
  const [openClientRow, setOpenClientRow] = useState<ClientLogRow | null>(null);
  // Captured on this device but not yet accepted by the cloud. Without it, "the cloud
  // has no events" and "this device has not delivered its events" render identically —
  // an empty feed, which is what made AUD-4 IMP-5 invisible for so long.
  const [undelivered, setUndelivered] = useState(0);
  useEffect(() => {
    void logger?.undeliveredCount?.().then(setUndelivered).catch(() => setUndelivered(0));
  }, [logger, clientRows]);

  // Fetch client logs — re-runs whenever filters change.
  // The Error tab pre-filters to warn/error levels by default; Activity shows all.
  const fetchClientLogs = async (overrides?: Partial<{
    customer: string; device: string; level: string; category: string;
    since: string; until: string; q: string;
  }>) => {
    setClientLoading(true);
    setClientError(null);
    try {
      const rawLevel = overrides?.level ?? filterLevel;
      const effectiveLevel = rawLevel || (mode === "errors" ? "warn,error" : "");
      const customer = (overrides?.customer ?? filterCustomer) || undefined;
      const device = (overrides?.device ?? filterDevice) || undefined;
      const level = effectiveLevel || undefined;
      const category = (overrides?.category ?? filterCategory) || undefined;
      const since = (overrides?.since ?? filterSince) || undefined;
      const until = (overrides?.until ?? filterUntil) || undefined;
      const q = (overrides?.q ?? searchQ) || undefined;
      // Device-local errors first — so app failures show even with NO cloud (offline,
      // cloud down, or not-yet-synced). Best-effort; never blocks the cloud rows.
      let local: ClientLogRow[] = [];
      if (logger?.recentLocalErrors) {
        local = await logger.recentLocalErrors().catch(() => []);
        if (mode === "errors") local = local.filter((r) => r.level === "error" || r.level === "warn");
        if (q) local = local.filter((r) => r.message.toLowerCase().includes(q.toLowerCase()));
      }
      let cloud: ClientLogRow[] = [];
      try {
        cloud = await client.dashboardClientLogs({ customer, device, level, category, since, until, q, limit: 200 });
        setClientError(null);
      } catch (e) {
        // Cloud unreachable — keep the local rows visible and note the cloud miss.
        setClientError(`Cloud log unavailable (showing this device only): ${e instanceof Error ? e.message : String(e)}`);
      }
      // Merge, cloud winning on duplicate ids, newest first.
      const byId = new Map<string, ClientLogRow>();
      for (const r of [...local, ...cloud]) byId.set(r.id, r);
      setClientRows(
        [...byId.values()].sort(
          (a, b) => isoToMs(b.occurred_at) - isoToMs(a.occurred_at),
        ),
      );
    } catch (e) {
      setClientError(e instanceof Error ? e.message : String(e));
    } finally {
      setClientLoading(false);
    }
  };

  // Initial load + re-fetch when mode changes
  useEffect(() => {
    void fetchClientLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Keep the client-event feed fresh while the panel is open (30s — matches the logger's
  // flush cadence). It previously refreshed ONLY on mount/filter/Refresh, so an error that
  // happened seconds ago looked like "nothing was logged" until a manual click.
  useEffect(() => {
    const id = setInterval(() => { void fetchClientLogs(); }, 30_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, filterCustomer, filterDevice, filterLevel, filterCategory, filterSince, filterUntil, searchQ]);

  // A since ≥ until range silently blanks the cloud feed — say so instead.
  const emptyRange = !!filterSince && !!filterUntil && filterSince >= filterUntil;

  const refresh = () => {
    void revalidate();
    void fetchClientLogs();
  };

  // Apply filter changes immediately (no debounce for selects/dates)
  const applyFilters = (patch: Partial<{
    customer: string; device: string; level: string; category: string;
    since: string; until: string; q: string;
  }>) => {
    if ("customer" in patch) setFilterCustomer(patch.customer ?? "");
    if ("device" in patch) setFilterDevice(patch.device ?? "");
    if ("level" in patch) setFilterLevel(patch.level ?? "");
    if ("category" in patch) setFilterCategory(patch.category ?? "");
    if ("since" in patch) setFilterSince(patch.since ?? "");
    if ("until" in patch) setFilterUntil(patch.until ?? "");
    if ("q" in patch) setSearchQ(patch.q ?? "");
    void fetchClientLogs(patch);
  };

  const hasAny = serverRows.length > 0 || clientRows.length > 0;

  return (
    <div data-testid={`obs-log-${mode}`}>
      {/* Toolbar: refresh + filter controls + search */}
      <div className="obs-filter-bar" data-testid="obs-filter-bar">
        <button type="button" className="btn btn--sm" onClick={refresh} disabled={loading || clientLoading} data-testid="obs-refresh">
          {loading || clientLoading ? "Loading…" : "Refresh"}
        </button>

        {/* Search */}
        <input
          type="search"
          placeholder="Search message…"
          value={searchQ}
          onInput={(e) => applyFilters({ q: (e.target as HTMLInputElement).value })}
          onChange={(e) => applyFilters({ q: e.target.value })}
          data-testid="clog-search"
        />

        {/* Level filter */}
        <select
          value={filterLevel}
          onChange={(e) => applyFilters({ level: e.target.value })}
          data-testid="clog-filter-level"
          aria-label="Filter by level"
        >
          <option value="">All levels</option>
          <option value="error">error</option>
          <option value="warn">warn</option>
          <option value="info">info</option>
          <option value="debug">debug</option>
        </select>

        {/* Category filter */}
        <select
          value={filterCategory}
          onChange={(e) => applyFilters({ category: e.target.value })}
          data-testid="clog-filter-category"
          aria-label="Filter by category"
        >
          <option value="">All categories</option>
          <option value="error">error</option>
          <option value="network">network</option>
          <option value="action">action</option>
          <option value="lifecycle">lifecycle</option>
        </select>

        {/* Customer filter */}
        <input
          type="text"
          placeholder="Customer…"
          value={filterCustomer}
          onChange={(e) => applyFilters({ customer: e.target.value })}
          data-testid="clog-filter-customer"
        />

        {/* Device filter */}
        <input
          type="text"
          placeholder="Device…"
          value={filterDevice}
          onChange={(e) => applyFilters({ device: e.target.value })}
          data-testid="clog-filter-device"
        />

        {/* Time range — LABELED: two bare datetime inputs gave no clue which was the start
            and which the end ("dates later in a blog is very weird"). */}
        <label className="obs-date-label">
          <span className="faint">From</span>
          <input
            type="datetime-local"
            value={filterSince}
            onChange={(e) => applyFilters({ since: e.target.value })}
            data-testid="clog-filter-since"
            aria-label="From (start of the time range)"
          />
        </label>
        <label className="obs-date-label">
          <span className="faint">To</span>
          <input
            type="datetime-local"
            value={filterUntil}
            onChange={(e) => applyFilters({ until: e.target.value })}
            data-testid="clog-filter-until"
            aria-label="To (end of the time range)"
          />
        </label>
        {(filterSince || filterUntil) && (
          <button
            type="button"
            className="btn btn--sm btn--ghost"
            onClick={() => applyFilters({ since: "", until: "" })}
            data-testid="clog-clear-range"
          >
            Clear dates
          </button>
        )}
      </div>

      {(error || clientError) && <p role="alert" data-testid="obs-error">{error ?? clientError}</p>}
      {emptyRange && (
        <p role="alert" data-testid="obs-empty-range">
          The time range is empty (Since is not before Until) — the cloud feed returns nothing. Clear or fix the dates.
        </p>
      )}

      {!hasAny ? (
        <EmptyState
          icon="list"
          title={(loading || clientLoading) ? "Loading…" : mode === "errors" ? "No errors logged" : "No activity yet"}
          hint="Generation jobs, AI calls, mock-up jobs, exports and client events appear here as they run."
          testId="obs-empty"
        />
      ) : (
        <>
          {/* Client EVENTS first — these are what error banners ("see the Error log") point
              at: sync failures, upload failures, render errors. They used to render BELOW
              the unbounded jobs table (or vanish entirely at 0 rows), so operators looking
              for a just-thrown error found "nothing". */}
          <h4 className="obs-section-title" data-testid="clog-title">
            Client events {clientLoading ? "· refreshing…" : `(${clientRows.length})`}
            {undelivered > 0 && (
              <span className="badge badge--warn" data-testid="clog-undelivered">
                {undelivered} undelivered
              </span>
            )}
            {clientRows.length > 0 && (
              <CopyButton
                className="btn btn--sm btn--ghost"
                value={clientRows.map(clogText).join("\n\n---\n\n")}
                label="Copy every loaded event (message, context, stack) as text"
                data-testid="clog-copy-all"
              >
                Copy all
              </CopyButton>
            )}
          </h4>
          {clientRows.length === 0 ? (
            <p className="faint" data-testid="clog-empty">
              No client events match the current filters. Events from other devices appear
              after that device uploads its logs (every ~30s while it is online and signed in).
            </p>
          ) : (
            <div className="table-scroll">
            <table className="table obs-table--click" data-testid="clog-table">
              <thead>
                <tr><th>Level</th><th>Category</th><th>Message</th><th>Device</th><th>Customer</th><th>When</th><th>Trace</th></tr>
              </thead>
              <tbody>
                {clientRows.map((r) => (
                  <tr key={`clog:${r.id}`} data-testid="clog-row">
                    <td>
                      <span className={`badge badge--plain ${r.level === "error" ? "badge--danger" : r.level === "warn" ? "badge--warn" : "badge--neutral"}`}>
                        {r.level}
                      </span>
                    </td>
                    <td className="faint">{r.category}</td>
                    <td>
                      <button type="button" className="link-btn" onClick={() => setOpenClientRow(r)} data-testid="clog-open">
                        {r.message.slice(0, 80)}{r.message.length > 80 ? "…" : ""}
                      </button>
                    </td>
                    <td className="faint"><code>{r.device_id.slice(0, 8)}</code></td>
                    <td className="faint">{r.customer_id ?? "—"}</td>
                    <td className="faint">{fmtTime(isoToMs(r.occurred_at))}</td>
                    <td>
                      <span className="row obs-row--tight">
                        {/* WS-Z9: `CopyButton` is already icon-only WITHOUT children — it has
                            drawn the copy glyph, its `aria-label` and its own tooltip on
                            twenty-odd surfaces. Dropping the word here is the whole change;
                            the label it announces is the full sentence, not the shorter
                            "Copy" the cell used to print. */}
                        <CopyButton
                          className="btn btn--sm btn--ghost"
                          value={clogText(r)}
                          label="Copy this event (message, context, stack)"
                          data-testid="clog-copy"
                        />
                        <RowAction label="Trace" icon="search" tone="quiet"
                          onClick={() => setOpenClientRow(r)} data-testid="clog-trace" />
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}

          {/* Server-derived rows (generation, ai_call, render, export) */}
          {serverRows.length > 0 && (
            <>
            <h4 className="obs-section-title" data-testid="obs-jobs-title">Jobs &amp; calls ({serverRows.length})</h4>
            <div className="table-scroll">
            <table className="table obs-table--click" data-testid="obs-table">
              <thead>
                <tr><th>Status</th><th>Kind</th><th>ID</th><th>Owner</th><th>Error</th><th>When</th><th>Trace</th></tr>
              </thead>
              <tbody>
                {serverRows.map((r) => (
                  <tr key={`${r.kind}:${r.id}`} data-testid="obs-row">
                    <td><span className={`badge badge--plain ${statusClass(r.status)}`}>{r.status}</span></td>
                    <td>{KIND_LABEL[r.kind] ?? r.kind}</td>
                    <td>
                      <button type="button" className="link-btn" onClick={() => setOpenRow(r)} data-testid="obs-open">
                        <code>{r.id.slice(0, 8)}</code>
                      </button>
                    </td>
                    <td className="faint">{r.ownerEmail ?? "—"}</td>
                    <td className="faint">{r.error ?? "—"}</td>
                    <td className="faint">{fmtTime(r.when)}</td>
                    <td>
                      <RowAction label="Trace" icon="search" tone="quiet"
                        onClick={() => setOpenRow(r)} data-testid="obs-trace" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            </>
          )}
        </>
      )}

      {openRow && <LogDetailDrawer row={openRow} onClose={() => setOpenRow(null)} />}
      {openClientRow && (
        <ClientLogDetailDrawer
          row={openClientRow}
          allRows={clientRows}
          onClose={() => setOpenClientRow(null)}
        />
      )}
    </div>
  );
}

/** One event as paste-ready text: every field the drawer shows, in plain lines —
 *  readable in a chat/issue as-is, unlike a JSON blob (context stays JSON since it
 *  is structured by nature). Shared by the row Copy, the header Copy all, and the
 *  drawer's Copy event. */
function clogText(r: ClientLogRow): string {
  const lines = [
    `[${r.level}] ${r.category} · ${new Date(isoToMs(r.occurred_at)).toLocaleString()}`,
    r.message,
    `device ${r.device_id} · session ${r.session_id}${r.customer_id ? ` · customer ${r.customer_id}` : ""} · v${r.app_version ?? "?"} ${r.platform ?? ""}`,
  ];
  if (r.context && Object.keys(r.context).length > 0) lines.push(`context: ${JSON.stringify(r.context)}`);
  if (r.stack) lines.push(`stack:\n${r.stack}`);
  return lines.join("\n");
}

/** Detail drawer for a single client-log event (Task 12). Shows message, stack,
 *  request details, a breadcrumb timeline (preceding events in the same session),
 *  and metadata (app_version, platform, device, customer). */
function ClientLogDetailDrawer({
  row,
  allRows: _allRows,
  onClose,
}: {
  row: ClientLogRow;
  allRows: ClientLogRow[];
  onClose: () => void;
}) {
  const { client } = useServices();
  const req = row.context?.request as
    | { method?: string; urlPath?: string; status?: number; durationMs?: number; ok?: boolean }
    | undefined;
  const requestId = typeof row.context?.requestId === "string" ? row.context.requestId : null;

  // Every event of THIS request — the client's network row plus the cloud's own
  // server-error row (category "server", device "cloud") when the request 500ed. This is
  // the trace-id view: one id, both sides of the failure.
  const [requestRows, setRequestRows] = useState<ClientLogRow[]>([]);
  useEffect(() => {
    if (!requestId) { setRequestRows([]); return; }
    void client
      .dashboardClientLogs({ request_id: requestId, limit: 50 })
      .then((rows) => setRequestRows(rows.filter((r) => r.id !== row.id)))
      .catch(() => {/* keep empty */});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id, requestId]);

  // Fetch the full session window at ALL levels so breadcrumbs include info/action
  // events even when the parent tab was filtered to warn/error only.
  const [sessionRows, setSessionRows] = useState<ClientLogRow[]>([]);
  useEffect(() => {
    const rowMs = isoToMs(row.occurred_at);
    const WINDOW_MS = 5 * 60 * 1000; // ±5 minutes
    const since = new Date(rowMs - WINDOW_MS).toISOString();
    const until = new Date(rowMs + WINDOW_MS).toISOString();
    void client.dashboardClientLogs({
      device: row.device_id,
      since,
      until,
      limit: 200,
    }).then((rows) => setSessionRows(rows)).catch(() => {/* keep empty */});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id]);

  // Breadcrumb timeline: events in the same session, older than this row, sorted ascending.
  const rowTs = isoToMs(row.occurred_at);
  const breadcrumbs = sessionRows
    .filter((r) => r.session_id === row.session_id && r.id !== row.id && isoToMs(r.occurred_at) < rowTs)
    .sort((a, b) => isoToMs(a.occurred_at) - isoToMs(b.occurred_at));

  const levelBadgeClass = row.level === "error" ? "badge--danger"
    : row.level === "warn" ? "badge--warn"
    : "badge--neutral";

  return (
    <Drawer
      title={<>Client log <code>{row.id.slice(0, 8)}</code></>}
      subtitle={`${row.category} · ${row.platform}`}
      badge={<span className={`badge ${levelBadgeClass}`}>{row.level}</span>}
      onClose={onClose}
      testid="clog-detail-drawer"
    >
      {/* Message */}
      <h4>Message</h4>
      <p data-testid="clog-message">{row.message}</p>

      {/* Stack trace */}
      {row.stack && (
        <>
          <h4>Stack trace</h4>
          <pre className="obs-trace__items obs-trace__items--error" data-testid="clog-stack">
            {row.stack}
          </pre>
        </>
      )}

      {/* Structured detail attached at the call site (step, url host, status, sizes, error…) */}
      {(() => {
        const detail = row.context?.detail as Record<string, unknown> | undefined;
        return detail && Object.keys(detail).length > 0 ? (
          <>
            <h4>Details</h4>
            <pre className="obs-trace__items" data-testid="clog-detail">
              {JSON.stringify(detail, null, 2)}
            </pre>
          </>
        ) : null;
      })()}

      {/* Request details */}
      {req && (
        <>
          <h4>Request</h4>
          <div className="fact-rows" data-testid="clog-request">
            {requestId && (
              <Fact
                label="Request id"
                value={<><code>{requestId}</code> <CopyButton value={requestId} label="Copy request id" /></>}
              />
            )}
            {req.method && <Fact label="Method" value={<code>{req.method}</code>} />}
            {req.urlPath && <Fact label="URL path" value={<code>{req.urlPath}</code>} />}
            {req.status != null && (
              <Fact
                label="Status"
                value={<span className={`badge ${req.ok ? "badge--success" : "badge--danger"}`}>{req.status}</span>}
              />
            )}
            {req.durationMs != null && <Fact label="Duration" value={fmtDur(req.durationMs)} />}
          </div>
        </>
      )}

      {/* Metadata */}
      <h4>Metadata <CopyButton className="btn btn--sm btn--ghost" value={clogText(row)} label="Copy this event (message, context, stack)" data-testid="clog-copy-event">Copy event</CopyButton></h4>
      <div className="fact-rows" data-testid="clog-metadata">
        <Fact label="App version" value={row.app_version} />
        <Fact label="Platform" value={row.platform} />
        <Fact label="Device" value={<><code>{row.device_id}</code> <CopyButton value={row.device_id} label="Copy device id" /></>} />
        {row.customer_id && <Fact label="Customer" value={row.customer_id} />}
        <Fact label="Session" value={<code>{row.session_id.slice(0, 8)}</code>} />
        <Fact label="When" value={fmtTime(isoToMs(row.occurred_at))} />
      </div>

      {/* The other side(s) of this request — server-recorded errors share the same id */}
      {requestRows.length > 0 && (
        <>
          <h4>Same request</h4>
          <ol className="obs-trace__list" data-testid="clog-request-rows">
            {requestRows.map((r) => (
              <li key={r.id} className={`obs-trace__evt obs-trace__evt--${r.level}`}>
                <span className="obs-trace__phase">{r.category}{r.device_id === "cloud" ? " · cloud" : ""}</span>
                <span className="obs-trace__msg">{r.message}</span>
                <span className="obs-trace__time faint">{fmtTime(isoToMs(r.occurred_at))}</span>
              </li>
            ))}
          </ol>
          {requestRows.some((r) => r.stack) && (
            <pre className="obs-trace__items obs-trace__items--error" data-testid="clog-request-stack">
              {requestRows.find((r) => r.stack)?.stack}
            </pre>
          )}
        </>
      )}

      {/* Breadcrumb timeline — preceding events in the same session */}
      {breadcrumbs.length > 0 && (
        <>
          <h4>Preceding events (same session)</h4>
          <ol className="obs-trace__list" data-testid="clog-breadcrumbs">
            {breadcrumbs.map((b) => (
              <li key={b.id} className={`obs-trace__evt obs-trace__evt--${b.level}`} data-testid="clog-breadcrumb-evt">
                <span className="obs-trace__phase">{b.category}</span>
                <span className="obs-trace__msg">{b.message}</span>
                <span className="obs-trace__time faint">{fmtTime(isoToMs(b.occurred_at))}</span>
              </li>
            ))}
          </ol>
        </>
      )}
    </Drawer>
  );
}

/**
 * Per-row detail drawer (items 13 error + 14 activity). One Drawer, dispatched by
 * `row.kind`, each populated from its OWN authoritative feed with REAL data:
 *   • render     → the captured trace + per-item loads/responses (dashboardJob).
 *   • generation → getGenerationJob(id): status, owner, created/finished, est cost,
 *                  error + the captured traceback (errorDetail) in a <pre>.
 *   • ai_call    → aiCallDetail(id): prompts, provider/model, tokens, latency,
 *                  status, error, cost_usd, owner_email.
 *   • export     → the row fields (the unified feed is the only source for exports).
 * Never fabricates a field — a missing value is omitted, not placeholdered.
 */
function LogDetailDrawer({ row, onClose }: { row: LogEntry; onClose: () => void }) {
  const subtitle = KIND_LABEL[row.kind] ?? row.kind;
  const badge = <span className={`badge ${statusClass(row.status)}`}>{row.status}</span>;
  const title = <>Log <code>{row.id.slice(0, 8)}</code></>;

  if (row.kind === "render") {
    return (
      <Drawer title={title} subtitle={subtitle} badge={badge} onClose={onClose} testid="obs-detail-drawer">
        <RenderTraceBody jobId={row.id} />
      </Drawer>
    );
  }
  if (row.kind === "generation") {
    return (
      <Drawer title={title} subtitle={subtitle} badge={badge} onClose={onClose} testid="obs-detail-drawer">
        <GenerationDetailBody jobId={row.id} />
      </Drawer>
    );
  }
  if (row.kind === "ai_call") {
    return (
      <Drawer title={title} subtitle={subtitle} badge={badge} onClose={onClose} testid="obs-detail-drawer">
        <AiCallDetailBody callId={row.id} />
      </Drawer>
    );
  }
  // export — only the unified-feed row carries data for this kind.
  return (
    <Drawer title={title} subtitle={subtitle} badge={badge} onClose={onClose} testid="obs-detail-drawer">
      <div className="fact-rows" data-testid="obs-detail-export">
        <Fact label="Id" value={<><code>{row.id}</code> <CopyButton value={row.id} label="Copy id" /></>} />
        <Fact label="Status" value={row.status} />
        {row.ownerEmail && <Fact label="Owner" value={row.ownerEmail} />}
        <Fact label="When" value={fmtTime(row.when)} />
      </div>
      {row.error && <><h4>Error</h4><pre className="obs-trace__items obs-trace__items--error">{row.error}</pre></>}
    </Drawer>
  );
}

/** Generation drawer body — pulls getGenerationJob(id) and shows only real fields. */
function GenerationDetailBody({ jobId }: { jobId: string }) {
  const { client } = useServices();
  const [job, setJob] = useState<GenerationJobView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const d = await client.getGenerationJob(jobId);
        if (!cancelled) setJob(d);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [client, jobId]);

  if (error) return <p role="alert">{error}</p>;
  if (!job) return <p className="faint">Loading…</p>;
  return (
    <>
      <div className="fact-rows" data-testid="obs-detail-generation">
        <Fact label="Job id" value={<><code>{job.id}</code> <CopyButton value={job.id} label="Copy job id" /></>} testid="gen-id" />
        <Fact label="Status" value={<span className={`badge ${statusClass(job.status)}`}>{job.status}</span>} />
        <Fact label="Type" value={job.jobType} />
        <Fact label="View" value={job.view} />
        {job.ownerEmail && <Fact label="Owner" value={job.ownerEmail} testid="gen-owner" />}
        {job.name && <Fact label="Name" value={job.name} testid="gen-name" />}
        {job.productType && <Fact label="Product type" value={job.productType} testid="gen-product-type" />}
        {job.brand && <Fact label="Brand" value={job.brand} testid="gen-brand" />}
        {job.model && <Fact label="Model" value={job.model} testid="gen-model" />}
        <Fact label="Created" value={fmtTime(isoToMs(job.createdAt) || undefined)} />
        <Fact label="Finished" value={fmtTime(isoToMs(job.finishedAt) || undefined)} />
        <Fact label="Est. cost" value={fmtCost(job.estCostUsd)} testid="gen-cost" />
      </div>
      {job.error && <><h4>Error</h4><pre className="obs-trace__items obs-trace__items--error" data-testid="gen-error">{job.error}</pre></>}
      {job.errorDetail && <><h4>Traceback</h4><pre className="obs-trace__items obs-trace__items--error" data-testid="gen-error-detail">{job.errorDetail}</pre></>}
    </>
  );
}

/** AI-call drawer body — pulls aiCallDetail(id); reuses the same fields as the AI-calls modal. */
function AiCallDetailBody({ callId }: { callId: string }) {
  const { client } = useServices();
  const [call, setCall] = useState<AiCallLogEntry | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const d = await client.aiCallDetail(callId);
        if (!cancelled) setCall(d);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [client, callId]);

  if (error) return <p role="alert">{error}</p>;
  if (!call) return <p className="faint">Loading…</p>;
  return (
    <>
      <div className="fact-rows" data-testid="obs-detail-aicall">
        <Fact label="Call id" value={<><code>{call.id}</code> <CopyButton value={call.id} label="Copy call id" /></>} testid="aicd-id" />
        <Fact label="Status" value={<span className={`badge ${call.status === "ok" ? "badge--success" : "badge--danger"}`}>{call.status}</span>} />
        <Fact label="Provider / model" value={<>{call.provider ?? "—"} · <code>{call.model ?? "—"}</code></>} />
        {call.owner_email && <Fact label="Owner" value={call.owner_email} testid="aicd-owner" />}
        <Fact label="Tokens (in)" value={call.input_tokens != null ? call.input_tokens.toLocaleString() : "—"} testid="aicd-tokens-in" />
        <Fact label="Tokens (out)" value={call.output_tokens != null ? call.output_tokens.toLocaleString() : "—"} testid="aicd-tokens-out" />
        <Fact label="Latency" value={call.latency_ms != null ? `${call.latency_ms}ms` : "—"} />
        {call.cost_usd != null && <Fact label="Cost" value={fmtCost(call.cost_usd)} testid="aicd-cost" />}
      </div>
      {call.error && <><h4>Error</h4><pre className="obs-trace__items obs-trace__items--error">{call.error}</pre></>}
      <h4>Prompt — system</h4>
      <pre className="obs-trace__items">{call.prompt_system || "—"}</pre>
      <h4>Prompt — user (sent)</h4>
      <pre className="obs-trace__items">{call.prompt_user || "—"}</pre>
    </>
  );
}

/** Render drawer body: the full flow for one render job — ordered traces +
 *  per-item load/response (dashboardJob). Body-only; the Drawer owns the chrome. */
function RenderTraceBody({ jobId }: { jobId: string }) {
  const { client } = useServices();
  const [job, setJob] = useState<JobRecord | null>(null);
  const [traces, setTraces] = useState<TraceEvent[]>([]);
  const [items, setItems] = useState<unknown[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const d = await client.dashboardJob(jobId);
        if (!cancelled) { setJob(d.job); setTraces(d.traces); setItems(d.items); }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [client, jobId]);

  return (
    <div data-testid="obs-detail-render">
      {error && <p role="alert">{error}</p>}
      {job && (
        <p className="faint">
          <span className={`badge ${statusClass(job.status)}`}>{job.status}</span>{" "}
          {job.designCount} designs · {fmtDur(job.durationMs)} · queued {fmtTime(job.queuedAt)}
        </p>
      )}

      <h4>Trace ({traces.length})</h4>
      {traces.length === 0 ? (
        <p className="faint">No trace events recorded for this job.</p>
      ) : (
        <ol className="obs-trace__list">
          {traces.map((t, i) => (
            <li key={i} className={`obs-trace__evt obs-trace__evt--${t.level}`} data-testid="obs-trace-evt">
              <span className="obs-trace__phase">{t.phase}{t.itemKey ? ` · ${t.itemKey}` : ""}</span>
              <span className="obs-trace__msg">{t.message}</span>
              <span className="obs-trace__time faint">
                {t.endedAt && t.startedAt ? fmtDur(t.endedAt - t.startedAt) : fmtTime(t.startedAt)}
              </span>
            </li>
          ))}
        </ol>
      )}

      <h4>Loads / responses ({items.length})</h4>
      {items.length === 0 ? (
        <p className="faint">No per-item results recorded.</p>
      ) : (
        <pre className="obs-trace__items" data-testid="obs-items">{JSON.stringify(items, null, 2)}</pre>
      )}
    </div>
  );
}

/**
 * `off` is DORMANT (configured away — an unused provider, local-only hosting); `unknown` is
 * NOT CHECKED. They are different statements and this board used to have only the former, so a
 * probe that had never run had nowhere honest to land and was rendered as a failure (WS-V).
 */
type HealthRow = { name: string; state: "up" | "down" | "off" | "unknown"; detail: string };

/** The database leg of a health report. `unchecked` means "we did not look", not "broken". */
function dbHealth(db: string): HealthRow["state"] {
  if (db === "ok") return "up";
  if (db === "unchecked") return "unknown";
  return "down";
}

/**
 * The startup seed leg (deep probe only). `pending` is the NORMAL early state of every
 * Postgres boot — the migrate+seed runs on a background thread after the container reports
 * healthy — so it is "not checked yet", not a fault.
 */
function seedHealth(seed: string): HealthRow["state"] {
  if (seed === "ok") return "up";
  if (seed === "incomplete") return "down";
  return "unknown";
}

function seedDetail(seed: string): string {
  if (seed === "ok") return "seed: ok";
  if (seed === "incomplete") {
    return "seed: incomplete — migrations applied, but seeding/bootstrap FAILED (check the container log)";
  }
  if (seed === "pending") return "seed: pending — the startup migrate+seed is still running";
  return `seed: ${seed}`;
}
/** Per-provider brokered-call volume (item 12) — "off" when no calls recorded. */
type ProviderUsageRow = { provider: string; records: number };

/** Health board — cloud API/DB, AI providers, hosting, desktop devices, throughput. */
type HealthSnapshot = {
  rows: HealthRow[];
  beats: HeartbeatPayload[];
  tput: { m1: number; m5: number; h1: number };
  usage: ProviderUsageRow[];
  checkedAt: number;
};

function HealthPanel() {
  const { client } = useServices();
  // Cache-first: keep the last health snapshot on screen; re-probe quietly every 60s.
  const { data, loading, revalidate } = useCachedResource<HealthSnapshot>(
    "dash.health",
    async () => {
      const out: HealthRow[] = [];
      let beats: HeartbeatPayload[] = [];
      let tput = { m1: 0, m5: 0, h1: 0 };
      try {
        const { jobs, heartbeats } = await client.dashboardActivity();
        beats = [...heartbeats].sort((a, b) => b.ts - a.ts);
        const now = Date.now();
        const within = (ms: number) => jobs.filter((j) => (j.startedAt ?? j.queuedAt) >= now - ms).length;
        tput = { m1: within(60_000), m5: within(300_000), h1: within(3_600_000) };
      } catch { /* keep zeros */ }
      try {
        // The DEEP probe. The shallow `/health` is DB-free BY DESIGN (cloud/app/main.py:542-560
        // — the container must report healthy before the background migration runs), so it
        // always answers `db: "unchecked"`; this board called it and rendered that as "down".
        // `?deep=1` actually runs `SELECT 1`, and also reports the startup seed. A 503 from it
        // is a readiness VERDICT carrying a full report, and `client.health` returns that body
        // rather than throwing — so an incomplete seed is named, not mistaken for a dead host.
        const h = await client.health({ deep: true });
        // This row is about REACHABILITY: the cloud answered, so it is up. What is degraded is
        // named by its own row below, rather than smeared across a row that cannot say which.
        out.push({ name: "Cloud API", state: "up", detail: `status: ${h.status}` });
        out.push({
          name: "Database",
          state: dbHealth(h.db),
          detail: `db: ${h.db} · migrations: ${h.migrations}`,
        });
        if (h.seed !== undefined) {
          out.push({ name: "Startup seed", state: seedHealth(h.seed), detail: seedDetail(h.seed) });
        }
      } catch (e) {
        // Only a genuine transport failure (or a 503 with no report in it) lands here.
        out.push({ name: "Cloud API", state: "down", detail: e instanceof Error ? e.message : "unreachable" });
        // NOT "down": we did not reach the API, so we learned NOTHING about the database —
        // saying "down · not checked" in one row is the same category error this defect was
        // about, one level in. The Cloud API row above already carries the actual failure.
        out.push({ name: "Database", state: "unknown", detail: "not checked (API unreachable)" });
      }
      try {
        const ai = await client.getAiSettings();
        const active = ai.active_provider;
        out.push({
          name: `AI provider (active: ${active})`,
          state: ai.keys_configured?.[active] ? "up" : "down",
          detail: ai.keys_configured?.[active] ? "key configured" : "no key for the active provider",
        });
        for (const [p, ok] of Object.entries(ai.keys_configured ?? {})) {
          if (p === active) continue;
          out.push({ name: `AI · ${p}`, state: ok ? "up" : "off", detail: ok ? "key configured" : "not configured" });
        }
      } catch (e) {
        out.push({ name: "AI providers", state: "down", detail: e instanceof Error ? e.message : "unreadable" });
      }
      try {
        const host = await client.getHostingSettings();
        out.push({
          name: "Hosting / CDN (R2)",
          state: host.r2_configured ? "up" : "off",
          detail: host.r2_configured ? `bucket: ${host.r2_bucket || "—"}` : "dormant (exports stay local)",
        });
      } catch {
        out.push({ name: "Hosting / CDN (R2)", state: "off", detail: "not configured" });
      }
      // Per-provider brokered-call volume (item 12) — real counts from the usage feed.
      let usage: ProviderUsageRow[] = [];
      try {
        const u: AiUsage = await client.aiUsage("provider");
        usage = (u.rows ?? [])
          .filter((r) => r.provider != null)
          .map((r) => ({ provider: String(r.provider), records: r.records ?? 0 }));
      } catch { /* keep empty */ }
      return { rows: out, beats, tput, usage, checkedAt: Date.now() };
    },
  );
  const rows = data?.rows ?? [];
  const beats = data?.beats ?? [];
  const tput = data?.tput ?? { m1: 0, m5: 0, h1: 0 };
  const usage = data?.usage ?? [];
  const checkedAt = data?.checkedAt ?? null;
  const probe = revalidate;

  const badge = (s: HealthRow["state"]) =>
    s === "up" ? "badge--success" : s === "down" ? "badge--danger" : "badge--neutral";
  const chip = (s: HealthRow["state"]) =>
    s === "up" ? "up" : s === "down" ? "down" : s === "unknown" ? "not checked" : "off";
  const onlineCount = beats.filter((b) => b.online).length;

  return (
    <div data-testid="obs-health">
      <div className="obs-stats">
        <button type="button" className="btn btn--sm" onClick={() => void probe()} disabled={loading} data-testid="obs-health-refresh">
          {loading ? "Checking…" : "Re-check"}
        </button>
        <button
          type="button"
          className="btn btn--sm btn--ghost"
          data-testid="obs-clear-cache"
          title="Clear this device's local cache (templates etc.) — forces a fresh load"
          onClick={() => {
            if (typeof window === "undefined" || window.confirm("Clear this device's local cache (incl. the downloaded AI model)? The app will reload data fresh.")) {
              clearAllCache();
              void clearModelCache();
              if (typeof window !== "undefined") window.location.reload();
            }
          }}
        >
          Clear local cache
        </button>
        {checkedAt && <span className="faint">last checked {fmtTime(checkedAt)}</span>}
      </div>

      {/* Connections (left) + Live throughput (right) — mock §358. Each is an `.inspector`
       *  (WS-Z4, rule 8); the status is a small chip and the detail is right-aligned. */}
      <div className="obs-health__top">
        <div className="inspector obs-card">
          <div className="inspector__head">
            <h3>Connections</h3>
            <span className="faint">{checkedAt ? `Re-checked ${fmtTime(checkedAt)}` : "Not yet checked"}</span>
          </div>
          <div className="inspector__body">
            <div data-testid="obs-health-table">
              {rows.map((r) => (
                <div key={r.name} className="obs-conn-row" data-testid="obs-health-row">
                  <span className="obs-conn-row__name">{r.name}</span>
                  <span className={`badge badge--plain ${badge(r.state)}`}>{chip(r.state)}</span>
                  <span className="obs-conn-row__detail">{r.detail}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="inspector obs-card">
          <div className="inspector__head">
            <h3>Live throughput</h3>
            <span className="faint">jobs</span>
          </div>
          <div className="inspector__body">
            <div className="obs-tput" data-testid="obs-throughput">
              <div className="obs-tput__card"><span className="obs-tput__value">{tput.m1}</span><span className="obs-tput__label">last 1 min</span></div>
              <div className="obs-tput__card"><span className="obs-tput__value">{tput.m5}</span><span className="obs-tput__label">last 5 min</span></div>
              <div className="obs-tput__card"><span className="obs-tput__value">{tput.h1}</span><span className="obs-tput__label">last hour</span></div>
              <div className="obs-tput__card"><span className="obs-tput__value">{onlineCount}</span><span className="obs-tput__label">devices online</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* AI providers · brokered calls (app extra) — a compact panel sharing the look. */}
      <div className="inspector obs-card obs-card--spaced">
        <div className="inspector__head"><h3>AI providers · brokered calls</h3></div>
        <div className="inspector__body">
          {usage.length === 0 ? (
            <p className="faint" data-testid="obs-no-usage">No brokered AI calls recorded yet.</p>
          ) : (
            <div className="table-scroll">
            <table className="table" data-testid="obs-usage-table">
              <thead><tr><th>Provider</th><th>Status</th><th className="obs-num">Calls</th></tr></thead>
              <tbody>
                {usage.map((u) => (
                  <tr key={u.provider} data-testid="obs-usage-row">
                    <td><strong>{u.provider}</strong></td>
                    <td><span className={`badge badge--plain ${u.records > 0 ? "badge--success" : "badge--neutral"}`}>{u.records > 0 ? "active" : "off"}</span></td>
                    <td className="obs-num">{u.records.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      {/* Desktop devices — full-width table (mock §377), in the same panel frame. */}
      <div className="inspector obs-card">
        <div className="inspector__head">
          <h3>Desktop devices</h3>
          <span className="faint">{beats.length} heartbeats</span>
        </div>
        <div className="inspector__body">
          {beats.length === 0 ? (
            <p className="faint" data-testid="obs-no-devices">No desktop heartbeats recorded yet.</p>
          ) : (
            <div className="table-scroll">
            <table className="table" data-testid="obs-devices-table">
              <thead><tr><th>Device</th><th>State</th><th>App / engine</th><th>Active job</th><th>Last beat</th></tr></thead>
              <tbody>
                {beats.map((b) => (
                  <tr key={b.deviceId} data-testid="obs-device-row">
                    <td><code>{b.deviceId.slice(0, 8)}</code> <CopyButton value={b.deviceId} label="Copy device id" /></td>
                    <td><span className={`badge badge--plain ${b.online ? "badge--success" : "badge--neutral"}`}>{b.online ? "online" : "offline"}</span></td>
                    <td className="faint">v{b.appVersion} · eng {b.engineVersion}</td>
                    <td className="obs-num">
                      {b.activeJobId
                        ? <span><code>{b.activeJobId.slice(0, 8)}</code>{b.progress != null ? ` · ${Math.round(b.progress * 100)}%` : ""}</span>
                        : <span className="faint">idle</span>}
                    </td>
                    <td className="faint">{fmtTime(b.ts)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
